import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

interface CspViolationReport {
  blockedURI?: string;
  violatedDirective?: string;
  originalPolicy?: string;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: string;
  userAgent?: string;
  url?: string;
  type?: string;
  body?: unknown;
}

interface ReportingObserverEntry {
  type: string;
  url: string;
  body: unknown;
}

interface ReportingObserverConstructor {
  new(callback: (reports: ReportingObserverEntry[]) => void): { observe(): void };
}

@Injectable({
  providedIn: 'root'
})
export class CspViolationService {
  private http = inject(HttpClient);

  constructor() {
    this.setupViolationReporting();
  }

  private setupViolationReporting(): void {
    // Listen for CSP violations
    document.addEventListener('securitypolicyviolation', (event) => {
      this.reportViolation({
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        originalPolicy: event.originalPolicy,
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        columnNumber: event.columnNumber,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    });

    // Also listen for report-to API violations (newer browsers)
    if ('ReportingObserver' in window) {
      const observer = new ((window as unknown as { ReportingObserver: ReportingObserverConstructor }).ReportingObserver)((reports: ReportingObserverEntry[]) => {
        reports.forEach(report => {
          if (report.type === 'csp-violation') {
            this.reportViolation({
              type: 'csp-violation',
              url: report.url,
              body: report.body,
              timestamp: new Date().toISOString()
            });
          }
        });
      });
      observer.observe();
    }
  }

  private reportViolation(violation: CspViolationReport): void {
    // Send violation report to backend for analysis
    this.http.post('/security/csp-violation', violation).subscribe({
      next: () => { },
      error: (error) => { }
    });
  }

  /**
   * Manually report a security concern
   */
  reportSecurityConcern(concern: {
    type: string;
    description: string;
    context?: Record<string, unknown>;
  }): void {
    const report = {
      ...concern,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    this.http.post('/security/report-concern', report).subscribe({
      error: (error) => { }
    });
  }
}
